/**
 * Filter modal for the Recurring transactions page.
 *
 * Opens from the Filter button in RecurringToolbar. React Portal
 * into document.body, centered on viewport, dim backdrop.
 *
 * Two dropdowns: Template Type (All / Scheduled / Reminder /
 * Unscheduled) and Transaction Type (All / Expense / Bill /
 * Invoice / Journal entry / Transfer). Reset clears both to
 * "All". Apply commits the choices to the parent and closes.
 *
 * Pure controlled component: parent owns the committed values
 * (used to filter the list) AND the draft values (what's in the
 * dropdowns right now). Local draft state here would complicate
 * the "cancel discards changes" flow; parent-controlled is simpler.
 *
 * Close via: Apply, Reset + Apply, X button, backdrop click, Escape.
 */

import { useEffect, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import type { RecurringMode, RecurringTxnType } from "../recurringTypes";
import { MODE_LABELS, TXN_TYPE_LABELS } from "../recurringMockData";

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  bgFade: "#F3F5F5",
};

export type TemplateTypeFilter = "all" | RecurringMode;
export type TransactionTypeFilter = "all" | RecurringTxnType;

interface FilterModalProps {
  isOpen: boolean;
  templateType: TemplateTypeFilter;
  transactionType: TransactionTypeFilter;
  onApply: (
    templateType: TemplateTypeFilter,
    transactionType: TransactionTypeFilter,
  ) => void;
  onClose: () => void;
}

export function FilterModal({
  isOpen,
  templateType,
  transactionType,
  onApply,
  onClose,
}: FilterModalProps) {
  // Local draft while modal is open; parent values are the committed source.
  const [draftTemplate, setDraftTemplate] =
    useState<TemplateTypeFilter>(templateType);
  const [draftTxn, setDraftTxn] = useState<TransactionTypeFilter>(transactionType);

  // Reset local drafts when the modal opens so each open starts from the
  // currently committed values, not whatever the user typed last time.
  useEffect(() => {
    if (isOpen) {
      setDraftTemplate(templateType);
      setDraftTxn(transactionType);
    }
  }, [isOpen, templateType, transactionType]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleReset = () => {
    setDraftTemplate("all");
    setDraftTxn("all");
  };

  const handleApply = () => {
    onApply(draftTemplate, draftTxn);
    onClose();
  };

  return createPortal(
    <div
      style={overlayStyle}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Filter recurring transactions"
    >
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          style={closeBtnStyle}
          aria-label="Close filter"
        >
          <X size={16} color={PALETTE.ink3} strokeWidth={2} />
        </button>

        <h3 style={titleStyle}>Recurring Transactions</h3>

        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="filter-template-type">
            Template Type
          </label>
          <select
            id="filter-template-type"
            value={draftTemplate}
            onChange={(e) =>
              setDraftTemplate(e.target.value as TemplateTypeFilter)
            }
            style={selectStyle}
          >
            <option value="all">All</option>
            <option value="scheduled">{MODE_LABELS.scheduled}</option>
            <option value="reminder">{MODE_LABELS.reminder}</option>
            <option value="unscheduled">{MODE_LABELS.unscheduled}</option>
          </select>
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="filter-txn-type">
            Transaction Type
          </label>
          <select
            id="filter-txn-type"
            value={draftTxn}
            onChange={(e) =>
              setDraftTxn(e.target.value as TransactionTypeFilter)
            }
            style={selectStyle}
          >
            <option value="all">All</option>
            <option value="expense">{TXN_TYPE_LABELS.expense}</option>
            <option value="bill">{TXN_TYPE_LABELS.bill}</option>
            <option value="invoice">{TXN_TYPE_LABELS.invoice}</option>
            <option value="journal">{TXN_TYPE_LABELS.journal}</option>
            <option value="transfer">{TXN_TYPE_LABELS.transfer}</option>
          </select>
        </div>

        <div style={actionsStyle}>
          <button type="button" style={resetBtnStyle} onClick={handleReset}>
            Reset
          </button>
          <button type="button" style={applyBtnStyle} onClick={handleApply}>
            Apply
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(14, 26, 26, 0.42)",
  zIndex: 2000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
};

const modalStyle: CSSProperties = {
  background: "#ffffff",
  borderRadius: 14,
  maxWidth: 440,
  width: "100%",
  padding: "22px 24px",
  boxShadow: "0 24px 48px -6px rgba(14, 26, 26, 0.3)",
  position: "relative",
};

const closeBtnStyle: CSSProperties = {
  position: "absolute",
  top: 14,
  right: 14,
  background: "transparent",
  border: 0,
  width: 28,
  height: 28,
  borderRadius: 6,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
};

const titleStyle: CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  color: PALETTE.ink,
  margin: "0 0 18px",
};

const fieldStyle: CSSProperties = {
  marginBottom: 14,
  display: "flex",
  flexDirection: "column",
};

const labelStyle: CSSProperties = {
  fontSize: 12,
  color: PALETTE.ink3,
  fontWeight: 500,
  marginBottom: 6,
};

const selectStyle: CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 8,
  fontSize: 13,
  color: PALETTE.ink,
  background: "#ffffff",
  outline: "none",
  cursor: "pointer",
  fontFamily: "inherit",
};

const actionsStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: 20,
};

const resetBtnStyle: CSSProperties = {
  background: "transparent",
  border: 0,
  color: PALETTE.ink3,
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
  padding: "8px 4px",
  fontFamily: "inherit",
};

const applyBtnStyle: CSSProperties = {
  background: PALETTE.ink,
  color: "#ffffff",
  border: 0,
  fontSize: 13,
  fontWeight: 600,
  padding: "10px 20px",
  borderRadius: 8,
  cursor: "pointer",
  fontFamily: "inherit",
};